import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  COMMON_IMPORTS,
  MATERIAL_IMPORTS,
  PIPES_IMPORTS,
} from '../../shared/imports/common-imports';
import { RiskScoreDiagramComponent } from '../../shared/components/risk-score-diagram/risk-score-diagram.component';
import { LambdaApiService } from '../../shared/services/lambda-api.service';
import {
  ComplianceData,
  ComplianceElement,
  RiskData,
  RiskFlagData,
} from '../../shared/models/compliance-data.model';
import { DialogService } from '../../shared/services/dialog.service';
import { DesanitizePipe } from '../../shared/pipes/desanitize.pipe';
import { BreadcrumbComponent } from '../../shared/components/breadcrumb/breadcrumb.component';
import { TaOverviewComponent } from '../ta-overview/ta-overview.component';
import { ComplianceElementDetailComponent } from '../compliance-element-detail/compliance-element-detail.component';
import { RiskFlagDetailComponent } from '../risk-flag-detail/risk-flag-detail.component';
import { THERAPEUTIC_AREAS } from '../../shared/data/therapeutic-areas';
import { AuthService } from '../../core/auth/services/auth.service';
import { UserRole } from '../../core/auth/models/profile';
import { FlagType } from '../../shared/models/top-vendors-data.model';
import { EmptyVendorFlagsPercentages } from '../../shared/models/percentages-response.model';
import { RiskScoreService } from '../../shared/services/risk-score.service';
import ComplianceElements from '../../shared/data/compliance-elements';
import { FilterVisualizerComponent } from '../../shared/components/filter-visualizer/filter-visualizer.component';
import { CompareComponent } from '../compare/compare.component';

@Component({
  selector: 'app-ta-detail',
  standalone: true,
  imports: [
    MATERIAL_IMPORTS,
    COMMON_IMPORTS,
    PIPES_IMPORTS,
    RiskScoreDiagramComponent,
    BreadcrumbComponent,
    TaOverviewComponent,
    ComplianceElementDetailComponent,
    RiskFlagDetailComponent,
    FilterVisualizerComponent,
    CompareComponent,
  ],
  providers: [DesanitizePipe],
  templateUrl: './ta-detail.component.html',
  styleUrl: './ta-detail.component.scss',
})
export class TaDetailComponent {
  therapeuticArea = '';
  complianceElement = ComplianceElements[0];
  riskFlag = '';
  data!: ComplianceData;
  title = 'Overview';
  isLoading = false;
  taMap = THERAPEUTIC_AREAS;
  currentView: 'therapeuticArea' | 'complianceElement' | 'riskFlag' =
    'therapeuticArea';
  displayFilterButton = false;
  displayCompareButton = false;
  percentages = [
    {
      flagType: FlagType.OPERATIONAL,
      percentage: EmptyVendorFlagsPercentages,
    },
    {
      flagType: FlagType.FINANCIAL,
      percentage: EmptyVendorFlagsPercentages,
    },
  ];
  percentageSum = { ...EmptyVendorFlagsPercentages };
  flagCount = 0;

  constructor(
    private readonly apiService: LambdaApiService,
    private readonly dialogService: DialogService,
    private readonly authService: AuthService,
    private readonly riskScoreService: RiskScoreService,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.isLoading = true;

      // Reset all state variables to prevent stale data from previous navigation
      this.percentageSum = { ...EmptyVendorFlagsPercentages };
      this.flagCount = 0;
      this.percentages = [
        {
          flagType: FlagType.OPERATIONAL,
          percentage: EmptyVendorFlagsPercentages,
        },
        {
          flagType: FlagType.FINANCIAL,
          percentage: EmptyVendorFlagsPercentages,
        },
      ];

      this.therapeuticArea = params.get('ta') ?? '';
      const elementName = params.get('element') ?? '';
      this.riskFlag = params.get('flag') ?? '';
      this.complianceElement = ComplianceElements[0];

      if (this.riskFlag) {
        this.currentView = 'riskFlag';
      } else if (elementName) {
        this.currentView = 'complianceElement';
        const complianceElement = ComplianceElements.find(
          (el) => el.link.toLowerCase() === elementName.toLowerCase(),
        );

        if (complianceElement) {
          this.complianceElement = complianceElement;
        }
      } else {
        this.currentView = 'therapeuticArea';
      }

      this.title = this.setTitle();

      this.loadData();

      if (this.authService.dbUserData) {
        this.displayFilterButton =
          this.authService.dbUserData.role !== UserRole.TA_USER;
      }

      this.displayCompareButton = this.currentView === 'complianceElement';
    });
  }

  setTitle(): string {
    const ta = this.taMap.find(
      (t) => t.id.toLowerCase() === this.therapeuticArea.toLowerCase(),
    );

    return ta ? ta.name : 'Overview';
  }

  loadData() {
    const percentages = this.percentages.map((p) => {
      return this.apiService.getPercentages(p.flagType).then((data) => {
        p.percentage = {
          high: data.high,
          medium: data.medium,
          low: data.low,
        };
      });
    });

    const complianceDataPromise = this.apiService
      .fetchComplianceData()
      .then((data: RiskData) => {
        this.flagCount = this.formatData(data);
      });

    const promise = this.apiService
      .getData(this.therapeuticArea, this.complianceElement.name)
      .then((data) => {
        this.data = data;
        const ta = this.taMap.find(
          (t) => t.id.toLowerCase() === this.therapeuticArea.toLowerCase(),
        );
        if (ta) {
          this.data.therapeuticArea = { ...ta };
        }
      });

    Promise.all([...percentages, promise, complianceDataPromise])
      .then(() => {
        if (
          !this.therapeuticArea ||
          this.therapeuticArea !== 'BBU' ||
          this.complianceElement.name !== 'Assessing Risk'
        ) {
          return 0;
        }

        this.percentageSum = this.riskScoreService.sumPercentages(
          this.percentages.map((p) => p.percentage),
        );

        //temporarly set the percentages to the "Assessing Risk" element
        //MVP only has data for this element
        const assesingRiskElement = this.data.elements.find(
          (e) => e.elementName === 'Assessing Risk',
        );

        if (assesingRiskElement) {
          assesingRiskElement.percentages = this.percentageSum;

          //re calculate colors
          const colors = this.riskScoreService.calculatePercentageColor(
            this.percentageSum,
          );
          assesingRiskElement.color = colors.color;
          assesingRiskElement.colorName = colors.colorName;
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  formatData(data: RiskData): number {
    if (
      !this.therapeuticArea ||
      this.therapeuticArea !== 'BBU' ||
      this.complianceElement.name !== 'Assessing Risk'
    ) {
      return 0;
    }

    const flags: RiskFlagData = {
      f1: 0,
      f2: 0,
      f3: 0,
      f4: 0,
      f5: 0,
      f6: 0,
    };

    data.regions.forEach((region) => {
      flags.f1 += region.totals.f1;
      flags.f2 += region.totals.f2;
      flags.f3 += region.totals.f3;
      flags.f4 += region.totals.f4;
      flags.f5 += region.totals.f5;
      flags.f6 += region.totals.f6;
    });

    return Object.values(flags).reduce((sum, val) => sum + val, 0);
  }

  openDateRangePicker() {
    const dialogRef = this.dialogService.openDateRangePicker();

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.refresh) {
        this.reloadData();
      }
    });
  }

  openFilterDialog() {
    const dialogRef = this.dialogService.openFilterDialog();

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.refresh) {
        this.reloadData();
      }
    });
  }

  openCompareDialog() {
    this.dialogService.openCompareDialog(
      this.therapeuticArea,
      this.complianceElement.link,
    );
  }

  reloadData() {
    this.isLoading = true;
    this.loadData();
  }
}
